import clsx from "clsx";
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Icon } from "@tsmono/react/icons";

import { AutogrowText } from "./AutogrowText";
import { EditAnnotationsDialog } from "./EditAnnotationsDialog";
import sharedStyles from "./EditAnnotationsDialog.module.css";
import styles from "./EditMetadataDialog.module.css";

type NewType = "string" | "number" | "boolean" | "object" | "array" | "null";

const kNewTypes: readonly NewType[] = [
  "string",
  "number",
  "boolean",
  "object",
  "array",
  "null",
];

const asNewType = (value: string): NewType =>
  kNewTypes.find((type) => type === value) ?? "string";

export interface MetaEntry {
  key: string;
  // String form used in the textarea. We persist as a string and parse on
  // save so users can type plain text or JSON freely.
  text: string;
  // Tracks the user's chosen type for the value: `true` means the value
  // should be saved as a raw string (no JSON parse), so that a string
  // tag with content `43` round-trips as `"43"` rather than the number
  // `43`. Set from `typeof value === "string"` when loading existing
  // metadata, or from the type dropdown when a key is added.
  isString?: boolean;
  isNew?: boolean;
  // Whether the value text has been touched since the row was loaded /
  // added. Used to drive the "Editing" change-summary line.
  dirty?: boolean;
}

interface EditMetadataDialogProps {
  showing: boolean;
  setShowing: (showing: boolean) => void;
  currentMetadata: Record<string, unknown>;
  logFile: string;
  onSaved?: () => void;
}

// Serialize any metadata value to its on-the-wire string form for editing.
// Strings stay as-is (no quotes); everything else is JSON-stringified so
// users can edit it as text. The reverse is applied on save.
const toEditableString = (v: unknown): string => {
  if (typeof v === "string") return v;
  return JSON.stringify(v, null, 2);
};

/**
 * Thrown by {@link serializeEntry} when a non-string entry's text
 * isn't well-formed JSON. Its message is shown in the dialog, naming
 * the row to fix. Common cause: JS-style object shorthand (`{a: 1}`)
 * instead of JSON (`{"a": 1}`).
 */
export class MetadataParseError extends Error {
  constructor(
    public readonly key: string,
    public readonly text: string,
    public readonly cause: unknown
  ) {
    super(
      `Invalid JSON for "${key}". Use JSON syntax — quote keys and ` +
        `strings, e.g. {"a": 1} or "yes".`
    );
    this.name = "MetadataParseError";
  }
}

// Detects text whose leading character unambiguously signals JSON
// syntax: `{` for objects, `[` for arrays, `"` for explicit strings.
// Such text always goes through `JSON.parse` regardless of the user's
// type-dropdown choice — typing `{a: 1}` is a clear attempt at an
// object literal, and silently saving it as the string `"{a: 1}"`
// (because the dropdown defaulted to `string`) hid the typo from the
// user. Primitives like `43`, `true`, or bare words respect the
// type dropdown.
const looksLikeJsonSyntax = (text: string): boolean => {
  const trimmed = text.trim();
  return (
    trimmed.startsWith("{") ||
    trimmed.startsWith("[") ||
    trimmed.startsWith('"')
  );
};

/**
 * Convert a dialog entry to its on-the-wire value, honoring the user's
 * chosen type *unless* the text carries JSON syntax (a leading `{`,
 * `[`, or `"`), in which case the structured intent wins:
 *
 *  - String-typed rows are saved verbatim when the text doesn't look
 *    structural. So a `string` row whose text is `43`, `approved`, or
 *    `true` round-trips as the string `"43"` / `"approved"` / `"true"`.
 *  - Anything else (other types, or string-typed but JSON-looking)
 *    goes through `JSON.parse`. Well-formed input lands as the proper
 *    JS value (`true`, `42`, `[1,2,3]`, `{"a": 1}`, `null`); malformed
 *    input throws {@link MetadataParseError} so the dialog can show a
 *    per-key message instead of saving garbage.
 *
 * Exported for unit testing.
 */
export const serializeEntry = (entry: MetaEntry): unknown => {
  if (entry.isString && !looksLikeJsonSyntax(entry.text)) {
    return entry.text;
  }
  try {
    return JSON.parse(entry.text);
  } catch (e) {
    throw new MetadataParseError(entry.key, entry.text, e);
  }
};

const buildMetadataSet = (entries: MetaEntry[]): Record<string, unknown> => {
  const metadata_set: Record<string, unknown> = {};
  for (const entry of entries) {
    metadata_set[entry.key] = serializeEntry(entry);
  }
  return metadata_set;
};

const seedFor = (type: NewType): unknown => {
  switch (type) {
    case "string":
      return "";
    case "number":
      return 0;
    case "boolean":
      return false;
    case "object":
      return {};
    case "array":
      return [];
    case "null":
      return null;
  }
};

export const EditMetadataDialog: FC<EditMetadataDialogProps> = ({
  showing,
  setShowing,
  currentMetadata,
  logFile,
  onSaved,
}) => {
  const initialEntries = useMemo<MetaEntry[]>(
    () =>
      Object.entries(currentMetadata).map(([key, value]) => ({
        key,
        text: toEditableString(value),
        // Anchors the type when re-serializing on save: a value loaded
        // as a string stays a string even if the user types digits.
        isString: typeof value === "string",
      })),
    [currentMetadata]
  );

  const [entries, setEntries] = useState<MetaEntry[]>(initialEntries);
  const [removed, setRemoved] = useState<string[]>([]);
  const [newKey, setNewKey] = useState("");
  const [newType, setNewType] = useState<NewType>("string");
  // Captures the most recently added key so an effect can scroll the
  // new row into view and focus its value textarea once it's in the
  // DOM. A ref (not state): it never needs to trigger a render — the
  // entries update from addKey re-runs the focus effect below.
  const pendingFocusKeyRef = useRef<string | null>(null);

  // Reset whenever the dialog opens (or the log's metadata changes under
  // us). Render-adjust rather than an effect so the reset lands before paint.
  const [prevReset, setPrevReset] = useState({ showing, initialEntries });
  if (
    prevReset.showing !== showing ||
    prevReset.initialEntries !== initialEntries
  ) {
    setPrevReset({ showing, initialEntries });
    if (showing) {
      setEntries(initialEntries);
      setRemoved([]);
      setNewKey("");
      setNewType("string");
    }
  }

  const existingKeys = useMemo(
    () => new Set(entries.map((e) => e.key)),
    [entries]
  );

  const adding = entries.filter((e) => e.isNew).map((e) => e.key);
  const editing = entries.filter((e) => e.dirty && !e.isNew).map((e) => e.key);

  const updateValue = useCallback((key: string, text: string) => {
    setEntries((prev) =>
      prev.map((e) => (e.key === key ? { ...e, text, dirty: true } : e))
    );
  }, []);

  const removeKey = useCallback((key: string) => {
    setEntries((prev) => {
      const entry = prev.find((e) => e.key === key);
      if (!entry) return prev;
      if (!entry.isNew) {
        setRemoved((r) => (r.includes(key) ? r : [...r, key]));
      }
      return prev.filter((e) => e.key !== key);
    });
  }, []);

  const addKey = () => {
    const k = newKey.trim();
    if (!k) return;
    if (existingKeys.has(k)) {
      // duplicate — silently no-op (matches design intent)
      setNewKey("");
      return;
    }
    const wasPreviouslyRemoved = removed.includes(k);
    if (wasPreviouslyRemoved) {
      setRemoved((r) => r.filter((x) => x !== k));
    }
    setEntries((prev) => [
      ...prev,
      {
        key: k,
        text: toEditableString(seedFor(newType)),
        // Remember the user's chosen type so the value is serialized
        // back the same way on save — picking `string` and typing `43`
        // must yield "43", not 43.
        isString: newType === "string",
        isNew: true,
        dirty: true,
      },
    ]);
    setNewKey("");
    setNewType("string");
    // Trigger a scroll-into-view + focus on the freshly inserted row
    // once it's in the DOM (handled by the effect below).
    pendingFocusKeyRef.current = k;
  };

  // Scroll the just-added row into view and focus its textarea so the
  // user can type the value immediately. Runs after the row's been
  // committed to the DOM; we look it up by `data-meta-key` rather than
  // taking a ref so we don't need to thread a ref handler through
  // every MetaRow.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const pendingFocusKey = pendingFocusKeyRef.current;
    if (pendingFocusKey == null) return;
    pendingFocusKeyRef.current = null;
    const selector = `[data-meta-key="${CSS.escape(pendingFocusKey)}"]`;
    const row = document.querySelector<HTMLElement>(selector);
    const textarea = row?.querySelector<HTMLTextAreaElement>("textarea");
    if (textarea) {
      // scrollIntoView walks up to the nearest scrollable ancestor —
      // here, the .tableScroll wrapper around the table.
      textarea.scrollIntoView({ behavior: "smooth", block: "nearest" });
      textarea.focus();
    }
  }, [entries]);

  const handleNewKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addKey();
    }
  };

  return (
    <EditAnnotationsDialog
      id="edit-metadata-dialog"
      title="Edit metadata"
      width="820px"
      showing={showing}
      setShowing={setShowing}
      logFile={logFile}
      onSaved={onSaved}
      changes={{ adding, editing, removing: removed }}
      buildEdit={() => ({
        type: "metadata",
        metadata_set: buildMetadataSet(
          entries.filter((e) => e.isNew || e.dirty)
        ),
        metadata_remove: removed,
      })}
    >
      {(submitting) => (
        <div className={sharedStyles.section}>
          <div className={sharedStyles.labelRow}>
            {/* Names the whole entry table, so it can't be a <label> — those
                may only point at a single form control. */}
            <span
              id="edit-metadata-label"
              className={clsx("text-size-smaller", sharedStyles.label)}
            >
              Metadata
            </span>
            <span className={clsx("text-size-smaller", sharedStyles.hint)}>
              Values are edited as plain text. Use JSON syntax for nested
              values.
            </span>
          </div>

          <div
            className={styles.tableScroll}
            role="group"
            aria-labelledby="edit-metadata-label"
          >
            <div className={styles.table}>
              {entries.length === 0 && (
                <div className={clsx("text-size-smaller", styles.empty)}>
                  No metadata yet — add a key below.
                </div>
              )}
              {entries.map((entry, idx) => (
                <MetaRow
                  key={entry.key}
                  entry={entry}
                  first={idx === 0}
                  onChange={(text) => updateValue(entry.key, text)}
                  onRemove={() => removeKey(entry.key)}
                  disabled={submitting}
                />
              ))}
            </div>
          </div>

          <div className={styles.addRow}>
            <input
              type="text"
              className={clsx(
                "form-control",
                "text-size-smaller",
                styles.addKeyInput
              )}
              placeholder="Add a key…"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              onKeyDown={handleNewKeyDown}
              disabled={submitting}
            />
            <select
              className={clsx(
                "form-select",
                "text-size-smaller",
                styles.typeSelect
              )}
              value={newType}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setNewType(asNewType(e.target.value))
              }
              aria-label="Type for new key"
              disabled={submitting}
            >
              <option value="string">string</option>
              <option value="number">number</option>
              <option value="boolean">boolean</option>
              <option value="object">object</option>
              <option value="array">array</option>
              <option value="null">null</option>
            </select>
            <button
              type="button"
              className={clsx(
                "btn",
                newKey.trim() ? "btn-primary" : "btn-secondary",
                "text-size-smaller",
                styles.addButton
              )}
              onClick={addKey}
              disabled={submitting || !newKey.trim()}
            >
              <Icon name="changeAdd" /> Add key
            </button>
          </div>
        </div>
      )}
    </EditAnnotationsDialog>
  );
};

const MetaRow: FC<{
  entry: MetaEntry;
  first: boolean;
  onChange: (text: string) => void;
  onRemove: () => void;
  disabled?: boolean;
}> = ({ entry, first, onChange, onRemove, disabled }) => (
  <div
    className={clsx(
      styles.row,
      !first && styles.rowDivider,
      entry.isNew && styles.rowNew
    )}
    // Used by the dialog's "scroll-to-new-key" effect to locate this
    // row's element via querySelector — keys are unique within the
    // editing session and are CSS.escape()'d on lookup so dots/quotes
    // are safe.
    data-meta-key={entry.key}
  >
    {/* Plain <span> not <code> — the global theme tints `<code>` with
        `--bs-code-color` (dark red) at higher specificity than a CSS
        module class. The monospace font comes from `.key`. */}
    <span className={clsx("text-size-smaller", styles.key)}>{entry.key}</span>
    <div className={styles.value}>
      <AutogrowText
        value={entry.text}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
    <button
      type="button"
      className={styles.remove}
      onClick={onRemove}
      title={`Remove ${entry.key}`}
      aria-label={`Remove ${entry.key}`}
      disabled={disabled}
    >
      <Icon name="trash" />
    </button>
  </div>
);
